import { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorBanner } from '../components/shared/ErrorBanner';
import { LoadingOverlay } from '../components/shared/LoadingOverlay';
import {
  useDeletedTemporaryMaterials,
  useDeletedCloudMaterials,
  useRestoreTemporaryMaterial,
  useRestoreCloudMaterial,
  usePermanentlyDeleteTemporaryMaterial,
  usePermanentlyDeleteCloudMaterial,
  useBulkDeleteTemporaryMaterials,
  useBulkDeleteCloudMaterials,
  type TrashFilters,
} from '../features/materials/hooks';
import {
  useDeletedScenarios,
  useRestoreScenario,
  usePermanentlyDeleteScenario,
  useBulkDeleteScenarios,
} from '../features/scenarios/hooks';
import type { ScenarioListItem } from '../features/scenarios/types';
import styles from './TrashPage.module.css';
import { calculateAspectRatio, createInfoBadges, formatDurationBadge, getVideoResolutionLabel } from '../components/materials/shared/materialUtils';
import { MaterialCard } from '../components/materials/shared/MaterialCard';
import { BulkActionsBar } from '../components/common/BulkActionsBar';
import { useToast } from '../hooks/useToast';
import { useUser } from '../features/auth/useUser';
import '../styles/buttons.css';

type TrashTab = 'materials' | 'scenarios';
type MaterialSource = 'temporary' | 'cloud';
type MaterialType = 'all' | 'image' | 'video';

type UnifiedMaterial = {
  id: string;
  source: MaterialSource;
  preview_url?: string;
  thumbnail_url?: string;
  temp_path?: string;
  storage_path?: string;
  content_type?: string;
  width?: number;
  height?: number;
  duration_seconds?: number;
  deleted_at?: string;
};

export default function TrashPage() {
  const [activeTab, setActiveTab] = useState<TrashTab>('materials');
  const [selectedSource, setSelectedSource] = useState<'all' | MaterialSource>('all');
  const [selectedType, setSelectedType] = useState<MaterialType>('all');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<string>('all');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [tempCursor, setTempCursor] = useState<string | null>(null);
  const [cloudCursor, setCloudCursor] = useState<string | null>(null);
  const [allTempItems, setAllTempItems] = useState<any[]>([]);
  const [allCloudItems, setAllCloudItems] = useState<any[]>([]);
  const [scenarioPage, setScenarioPage] = useState(1);

  // サーバーサイドフィルタ用のパラメータを構築
  const apiFilters: TrashFilters = useMemo(() => {
    const filters: TrashFilters = {};
    if (selectedType !== 'all') {
      filters.file_type = selectedType;
    }
    if (selectedAspectRatio !== 'all') {
      filters.aspect_ratio = selectedAspectRatio;
    }
    return filters;
  }, [selectedType, selectedAspectRatio]);

  const tempQuery = useDeletedTemporaryMaterials({ limit: 60, cursor: tempCursor, filters: apiFilters });
  const cloudQuery = useDeletedCloudMaterials({ limit: 60, cursor: cloudCursor, filters: apiFilters });
  const restoreTempMutation = useRestoreTemporaryMaterial();
  const restoreCloudMutation = useRestoreCloudMaterial();
  const permanentDeleteTempMutation = usePermanentlyDeleteTemporaryMaterial();
  const permanentDeleteCloudMutation = usePermanentlyDeleteCloudMaterial();
  const bulkDeleteTempMutation = useBulkDeleteTemporaryMaterials();
  const bulkDeleteCloudMutation = useBulkDeleteCloudMaterials();

  // シナリオゴミ箱
  const scenarioTrashQuery = useDeletedScenarios({ page: scenarioPage, limit: 50 });
  const restoreScenarioMutation = useRestoreScenario();
  const permanentDeleteScenarioMutation = usePermanentlyDeleteScenario();
  const bulkDeleteScenariosMutation = useBulkDeleteScenarios();

  const queryClient = useQueryClient();
  const toast = useToast();
  const { isAdmin } = useUser();

  // タブ切替時にbulk編集をリセット
  useEffect(() => {
    setBulkEditMode(false);
    setBulkSelectedIds(new Set());
  }, [activeTab]);

  // フィルタが変更されたらキャッシュをリセット
  useEffect(() => {
    setAllTempItems([]);
    setAllCloudItems([]);
    setTempCursor(null);
    setCloudCursor(null);
  }, [apiFilters]);

  // データが更新されたら、全アイテムリストに追加
  useEffect(() => {
    if (tempQuery.data?.items) {
      setAllTempItems((prev) => {
        // 既存のIDをセットで管理
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = tempQuery.data.items.filter((item: any) => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    }
  }, [tempQuery.data]);

  useEffect(() => {
    if (cloudQuery.data?.items) {
      setAllCloudItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = cloudQuery.data.items.filter((item: any) => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    }
  }, [cloudQuery.data]);

  // 個人フォルダと共有フォルダのデータを統合
  const unifiedMaterials = useMemo((): UnifiedMaterial[] => {
    const tempMaterials = allTempItems.map((item) => ({
      ...item,
      source: 'temporary' as MaterialSource,
    }));
    const cloudMaterials = allCloudItems.map((item) => ({
      ...item,
      source: 'cloud' as MaterialSource,
    }));
    return [...tempMaterials, ...cloudMaterials].sort((a, b) => {
      const dateA = a.deleted_at ? new Date(a.deleted_at).getTime() : 0;
      const dateB = b.deleted_at ? new Date(b.deleted_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [allTempItems, allCloudItems]);

  // フィルタリング（フォルダソースのみクライアントサイドでフィルタ）
  // file_typeとaspect_ratioはサーバーサイドでフィルタ済み
  const filteredMaterials = useMemo(() => {
    if (selectedSource === 'all') {
      return unifiedMaterials;
    }
    return unifiedMaterials.filter((item) => item.source === selectedSource);
  }, [unifiedMaterials, selectedSource]);

  const isBusy =
    tempQuery.isLoading ||
    cloudQuery.isLoading ||
    restoreTempMutation.isPending ||
    restoreCloudMutation.isPending ||
    permanentDeleteTempMutation.isPending ||
    permanentDeleteCloudMutation.isPending ||
    bulkDeleteTempMutation.isPending ||
    bulkDeleteCloudMutation.isPending ||
    restoreScenarioMutation.isPending ||
    permanentDeleteScenarioMutation.isPending ||
    bulkDeleteScenariosMutation.isPending;

  const handleRestore = (item: UnifiedMaterial) => {
    if (window.confirm('この素材を復元しますか?')) {
      if (item.source === 'temporary') {
        restoreTempMutation.mutate(item.id, {
          onSuccess: () => {
            toast.success('素材を復元しました');
            // ステートから削除してUIを即座に更新
            setAllTempItems((prev) => prev.filter((i) => i.id !== item.id));
            // クエリを無効化して次回取得時に最新データを取得
            queryClient.invalidateQueries({ queryKey: ['materials', 'temporary', 'trash'] });
          },
        });
      } else {
        restoreCloudMutation.mutate(item.id, {
          onSuccess: () => {
            toast.success('素材を復元しました');
            // ステートから削除してUIを即座に更新
            setAllCloudItems((prev) => prev.filter((i) => i.id !== item.id));
            // クエリを無効化して次回取得時に最新データを取得
            queryClient.invalidateQueries({ queryKey: ['materials', 'cloud', 'trash'] });
          },
        });
      }
    }
  };

  const handlePermanentDelete = (item: UnifiedMaterial) => {
    if (window.confirm('この素材を完全に削除しますか?\nこの操作は取り消せません。')) {
      if (item.source === 'temporary') {
        permanentDeleteTempMutation.mutate(item.id, {
          onSuccess: () => {
            toast.success('素材を完全に削除しました');
            // ステートから削除してUIを即座に更新
            setAllTempItems((prev) => prev.filter((i) => i.id !== item.id));
            // クエリを無効化して次回取得時に最新データを取得
            queryClient.invalidateQueries({ queryKey: ['materials', 'temporary', 'trash'] });
          },
          onError: (error: unknown) => {
            console.error('完全削除エラー:', error);
            if (error instanceof Error && 'scenario_titles' in error) {
              const scenarioTitles = (error as Error & { scenario_titles?: string[] }).scenario_titles;
              if (scenarioTitles && scenarioTitles.length > 0) {
                toast.error(`この素材はお気に入りシナリオ「${scenarioTitles[0]}」で使用中のため削除できません`);
                return;
              }
            }
            toast.error('削除に失敗しました');
          },
        });
      } else {
        permanentDeleteCloudMutation.mutate(item.id, {
          onSuccess: () => {
            toast.success('素材を完全に削除しました');
            // ステートから削除してUIを即座に更新
            setAllCloudItems((prev) => prev.filter((i) => i.id !== item.id));
            // クエリを無効化して次回取得時に最新データを取得
            queryClient.invalidateQueries({ queryKey: ['materials', 'cloud', 'trash'] });
          },
          onError: (error: unknown) => {
            console.error('完全削除エラー:', error);
            if (error instanceof Error && 'scenario_titles' in error) {
              const scenarioTitles = (error as Error & { scenario_titles?: string[] }).scenario_titles;
              if (scenarioTitles && scenarioTitles.length > 0) {
                toast.error(`この素材はお気に入りシナリオ「${scenarioTitles[0]}」で使用中のため削除できません`);
                return;
              }
            }
            toast.error('削除に失敗しました');
          },
        });
      }
    }
  };

  // 一括削除
  const handleBulkDelete = async () => {
    const count = bulkSelectedIds.size;
    if (count === 0) return;

    if (!window.confirm(`選択した${count}件の素材を完全に削除しますか?\nこの操作は取り消せません。`)) {
      return;
    }

    const selectedMaterials = filteredMaterials.filter((mat) => bulkSelectedIds.has(mat.id));
    const tempIds = selectedMaterials.filter((m) => m.source === 'temporary').map((m) => m.id);
    const cloudIds = selectedMaterials.filter((m) => m.source === 'cloud').map((m) => m.id);

    toast.show(`${count}件の素材を削除中...`);

    try {
      // 一括削除APIを使用
      const results = await Promise.all([
        tempIds.length > 0 ? bulkDeleteTempMutation.mutateAsync(tempIds) : Promise.resolve({ deleted_count: 0, failed_ids: [], protected_ids: [] }),
        cloudIds.length > 0 ? bulkDeleteCloudMutation.mutateAsync(cloudIds) : Promise.resolve({ deleted_count: 0, failed_ids: [], protected_ids: [] }),
      ]);

      const totalDeleted = results.reduce((acc, r) => acc + r.deleted_count, 0);
      const totalFailed = results.reduce((acc, r) => acc + r.failed_ids.length, 0);
      const totalProtected = results.reduce((acc, r) => acc + (r.protected_ids?.length || 0), 0);

      // ステートから削除してUIを即座に更新（保護された素材は除外）
      const allProtectedIds = new Set([...(results[0].protected_ids || []), ...(results[1].protected_ids || [])]);
      const deletedTempIds = new Set(tempIds.filter((id) => !results[0].failed_ids.includes(id) && !allProtectedIds.has(id)));
      const deletedCloudIds = new Set(cloudIds.filter((id) => !results[1].failed_ids.includes(id) && !allProtectedIds.has(id)));
      setAllTempItems((prev) => prev.filter((i) => !deletedTempIds.has(i.id)));
      setAllCloudItems((prev) => prev.filter((i) => !deletedCloudIds.has(i.id)));

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['materials', 'temporary', 'trash'] }),
        queryClient.invalidateQueries({ queryKey: ['materials', 'cloud', 'trash'] }),
      ]);

      if (totalProtected > 0) {
        toast.success(`${totalDeleted}件の素材を削除しました（${totalProtected}件はお気に入りシナリオで使用中のためスキップ）`);
      } else if (totalFailed > 0) {
        toast.success(`${totalDeleted}件の素材を削除しました（${totalFailed}件失敗）`);
      } else {
        toast.success(`${totalDeleted}件の素材を完全に削除しました`);
      }
      setBulkSelectedIds(new Set());
      setBulkEditMode(false);
    } catch (error) {
      toast.error('削除に失敗しました');
      console.error('Bulk delete error:', error);
    }
  };

  // ごみ箱を空にする
  const handleEmptyTrash = async () => {
    const totalCount = unifiedMaterials.length;
    if (totalCount === 0) return;

    if (!window.confirm(`ごみ箱内のすべての素材（${totalCount}件）を完全に削除しますか?\nこの操作は取り消せません。`)) {
      return;
    }

    const tempIds = unifiedMaterials.filter((m) => m.source === 'temporary').map((m) => m.id);
    const cloudIds = unifiedMaterials.filter((m) => m.source === 'cloud').map((m) => m.id);

    toast.show(`${totalCount}件の素材を削除中...`);

    try {
      // 一括削除APIを使用
      const results = await Promise.all([
        tempIds.length > 0 ? bulkDeleteTempMutation.mutateAsync(tempIds) : Promise.resolve({ deleted_count: 0, failed_ids: [], protected_ids: [] }),
        cloudIds.length > 0 ? bulkDeleteCloudMutation.mutateAsync(cloudIds) : Promise.resolve({ deleted_count: 0, failed_ids: [], protected_ids: [] }),
      ]);

      const totalDeleted = results.reduce((acc, r) => acc + r.deleted_count, 0);
      const totalFailed = results.reduce((acc, r) => acc + r.failed_ids.length, 0);
      const totalProtected = results.reduce((acc, r) => acc + (r.protected_ids?.length || 0), 0);

      // ステートから削除してUIを即座に更新（保護された素材は除外）
      const allProtectedIds = new Set([...(results[0].protected_ids || []), ...(results[1].protected_ids || [])]);
      const deletedTempIds = new Set(tempIds.filter((id) => !results[0].failed_ids.includes(id) && !allProtectedIds.has(id)));
      const deletedCloudIds = new Set(cloudIds.filter((id) => !results[1].failed_ids.includes(id) && !allProtectedIds.has(id)));
      setAllTempItems((prev) => prev.filter((i) => !deletedTempIds.has(i.id)));
      setAllCloudItems((prev) => prev.filter((i) => !deletedCloudIds.has(i.id)));

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['materials', 'temporary', 'trash'] }),
        queryClient.invalidateQueries({ queryKey: ['materials', 'cloud', 'trash'] }),
      ]);

      if (totalProtected > 0) {
        toast.success(`${totalDeleted}件の素材を削除しました（${totalProtected}件はお気に入りシナリオで使用中のためスキップ）`);
      } else if (totalFailed > 0) {
        toast.success(`${totalDeleted}件の素材を削除しました（${totalFailed}件失敗）`);
      } else {
        toast.success(`${totalDeleted}件の素材を完全に削除しました`);
      }
      setBulkSelectedIds(new Set());
      setBulkEditMode(false);
    } catch (error) {
      toast.error('削除に失敗しました');
      console.error('Empty trash error:', error);
    }
  };

  // user権限で削除可能な素材（個人フォルダまたはadmin）
  const selectableMaterials = useMemo(() => {
    return filteredMaterials.filter((m) => m.source === 'temporary' || isAdmin);
  }, [filteredMaterials, isAdmin]);

  // チェックボックストグル
  const handleToggleSelection = (id: string) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // すべて選択/解除（選択可能な素材のみ）
  const handleSelectAll = () => {
    if (bulkSelectedIds.size === selectableMaterials.length) {
      setBulkSelectedIds(new Set());
    } else {
      setBulkSelectedIds(new Set(selectableMaterials.map((m) => m.id)));
    }
  };

  // === シナリオゴミ箱のハンドラー ===

  const deletedScenarios: ScenarioListItem[] = scenarioTrashQuery.data?.items ?? [];

  const handleRestoreScenario = (scenario: ScenarioListItem) => {
    if (window.confirm(`シナリオ「${scenario.title}」を復元しますか?`)) {
      restoreScenarioMutation.mutate(scenario.id, {
        onSuccess: () => {
          toast.success('シナリオを復元しました');
          queryClient.invalidateQueries({ queryKey: ['scenarios'] });
        },
        onError: () => {
          toast.error('復元に失敗しました');
        },
      });
    }
  };

  const handlePermanentDeleteScenario = (scenario: ScenarioListItem) => {
    if (window.confirm(`シナリオ「${scenario.title}」を完全に削除しますか?\nこの操作は取り消せません。`)) {
      permanentDeleteScenarioMutation.mutate(scenario.id, {
        onSuccess: () => {
          toast.success('シナリオを完全に削除しました');
          queryClient.invalidateQueries({ queryKey: ['scenarios', 'trash'] });
        },
        onError: () => {
          toast.error('削除に失敗しました');
        },
      });
    }
  };

  const handleBulkDeleteScenarios = async () => {
    const count = bulkSelectedIds.size;
    if (count === 0) return;

    if (!window.confirm(`選択した${count}件のシナリオを完全に削除しますか?\nこの操作は取り消せません。`)) {
      return;
    }

    toast.show(`${count}件のシナリオを削除中...`);

    try {
      const result = await bulkDeleteScenariosMutation.mutateAsync(Array.from(bulkSelectedIds));
      await queryClient.invalidateQueries({ queryKey: ['scenarios', 'trash'] });

      if (result.failed_ids.length > 0) {
        toast.success(`${result.deleted_count}件のシナリオを削除しました（${result.failed_ids.length}件失敗）`);
      } else {
        toast.success(`${result.deleted_count}件のシナリオを完全に削除しました`);
      }
      setBulkSelectedIds(new Set());
      setBulkEditMode(false);
    } catch (error) {
      toast.error('削除に失敗しました');
      console.error('Bulk delete scenarios error:', error);
    }
  };

  const handleSelectAllScenarios = () => {
    if (bulkSelectedIds.size === deletedScenarios.length) {
      setBulkSelectedIds(new Set());
    } else {
      setBulkSelectedIds(new Set(deletedScenarios.map((s) => s.id)));
    }
  };

  const handleEmptyScenarioTrash = async () => {
    const totalCount = deletedScenarios.length;
    if (totalCount === 0) return;

    if (!window.confirm(`ごみ箱内のすべてのシナリオ（${totalCount}件）を完全に削除しますか?\nこの操作は取り消せません。`)) {
      return;
    }

    toast.show(`${totalCount}件のシナリオを削除中...`);

    try {
      const ids = deletedScenarios.map((s) => s.id);
      const result = await bulkDeleteScenariosMutation.mutateAsync(ids);
      await queryClient.invalidateQueries({ queryKey: ['scenarios', 'trash'] });
      toast.success(`${result.deleted_count}件のシナリオを完全に削除しました`);
      setBulkSelectedIds(new Set());
      setBulkEditMode(false);
    } catch (error) {
      toast.error('削除に失敗しました');
      console.error('Empty scenario trash error:', error);
    }
  };

  if (activeTab === 'materials' && (tempQuery.isError || cloudQuery.isError)) {
    return (
      <div className={styles.panel}>
        <ErrorBanner message="ゴミ箱の読み込みに失敗しました。" />
      </div>
    );
  }

  if (activeTab === 'scenarios' && scenarioTrashQuery.isError) {
    return (
      <div className={styles.panel}>
        <ErrorBanner message="ゴミ箱の読み込みに失敗しました。" />
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {isBusy && <LoadingOverlay />}

      {/* 1行目: タイトル */}
      <div className={styles.row1}>
        <div>
          <h1 className={styles.sectionTitle}>ゴミ箱</h1>
          <p className={styles.subtitle}>
            {activeTab === 'materials'
              ? '削除された素材は30日間保管されます。30日経過後、自動的に完全削除されます。'
              : '削除されたシナリオはここに保管されます。復元または完全削除ができます。'}
          </p>
        </div>
      </div>

      {/* タブ切替 */}
      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'materials' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('materials')}
        >
          素材
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'scenarios' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('scenarios')}
        >
          シナリオ
        </button>
      </div>

      {/* === 素材タブ === */}
      {activeTab === 'materials' && (
        <>
          {/* 2行目: アクションボタン */}
          <div className={styles.row2}>
            <div className={styles.leftActions}>
              <button
                type="button"
                className={`btn-modal ${bulkEditMode ? styles.actionButtonActive : ''}`}
                onClick={() => {
                  setBulkEditMode(!bulkEditMode);
                  if (bulkEditMode) {
                    setBulkSelectedIds(new Set());
                  }
                }}
              >
                {bulkEditMode ? '一括編集を終了' : '一括編集'}
              </button>
            </div>
            <div className={styles.rightActions}>
              <button
                type="button"
                className="btn-modal-danger"
                onClick={handleEmptyTrash}
                disabled={unifiedMaterials.length === 0}
              >
                ごみ箱を空にする
              </button>
            </div>
          </div>

          {/* 3行目: フィルター */}
          <div className={styles.filterSection}>
            <button
              type="button"
              className={styles.filterToggle}
              onClick={() => setFilterExpanded(!filterExpanded)}
            >
              <span className={styles.filterIcon}>{filterExpanded ? '▼' : '▶'}</span>
              <span className={styles.filterLabel}>フィルタ</span>
            </button>
            {filterExpanded && (
              <div className={styles.filterControls}>
                <div className={styles.filterRow}>
                  <select
                    className={styles.filterSelect}
                    value={selectedSource}
                    onChange={(e) => setSelectedSource(e.target.value as 'all' | MaterialSource)}
                  >
                    <option value="all">フォルダ: すべて</option>
                    <option value="temporary">個人フォルダ</option>
                    <option value="cloud">共有フォルダ</option>
                  </select>
                  <select
                    className={styles.filterSelect}
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as MaterialType)}
                  >
                    <option value="all">種別: すべて</option>
                    <option value="image">静止画</option>
                    <option value="video">動画</option>
                  </select>
                  <select
                    className={styles.filterSelect}
                    value={selectedAspectRatio}
                    onChange={(e) => setSelectedAspectRatio(e.target.value)}
                  >
                    <option value="all">アスペクト比: すべて</option>
                    <option value="16:9">16:9</option>
                    <option value="9:16">9:16</option>
                    <option value="1:1">1:1</option>
                    <option value="7:4">7:4</option>
                    <option value="4:7">4:7</option>
                    <option value="1.77:1">1.77:1</option>
                    <option value="1:1.77">1:1.77</option>
                    <option value="other">その他</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* 一括編集バー */}
          {bulkEditMode && (
            <BulkActionsBar
              selectedCount={bulkSelectedIds.size}
              onCancel={() => {
                setBulkEditMode(false);
                setBulkSelectedIds(new Set());
              }}
            >
              <button
                type="button"
                className="bulk-action-btn"
                onClick={handleSelectAll}
              >
                {bulkSelectedIds.size === filteredMaterials.length ? 'すべて解除' : 'すべて選択'}
              </button>
              <button
                type="button"
                className="bulk-action-btn bulk-delete-btn"
                onClick={handleBulkDelete}
                disabled={bulkSelectedIds.size === 0}
              >
                完全に削除
              </button>
            </BulkActionsBar>
          )}

          {/* 素材グリッド */}
          <div className={styles.content}>
            {filteredMaterials.length === 0 ? (
              <div className={styles.emptyState}>
                <p>ゴミ箱は空です</p>
              </div>
            ) : (
              <div className={styles.trashGrid}>
                {filteredMaterials.map((item) => {
                  const isVideo = item.content_type?.startsWith('video/');
                  const sizeLabel = item.width && item.height ? `${item.width}×${item.height}` : 'サイズ不明';
                  const durationLabel = isVideo ? formatDurationBadge(item.duration_seconds) ?? '秒数不明' : null;
                  const resolutionLabel = isVideo ? getVideoResolutionLabel(item.width, item.height) : null;
                  const folderLabel = item.source === 'temporary' ? '個人フォルダ' : '共有フォルダ';

                  // アスペクト比バッジを最初に追加
                  const aspectRatioBadge = calculateAspectRatio(item.width, item.height);
                  const badgeLabels = isVideo
                    ? [aspectRatioBadge, durationLabel, resolutionLabel, folderLabel].filter(Boolean)
                    : [aspectRatioBadge, sizeLabel, folderLabel].filter(Boolean);
                  const deletedDateTime = item.deleted_at
                    ? new Date(item.deleted_at).toLocaleString('ja-JP', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })
                    : '';

                  // 共有フォルダの素材はadmin権限が必要
                  const canPermanentDelete = item.source === 'temporary' || isAdmin;

                  return (
                    <MaterialCard
                      key={`${item.source}-${item.id}`}
                        imageUrl={item.thumbnail_url || item.preview_url || item.temp_path || item.storage_path}
                        contentType={item.content_type}
                        width={item.width}
                        height={item.height}
                        previewAlt={`削除済み素材 ${item.id}`}
                        infoBadges={createInfoBadges(badgeLabels, item.source === 'temporary' ? ['個人フォルダ'] : [])}
                        isActive={false}
                        isMarked={bulkSelectedIds.has(item.id)}
                        checkboxDisabled={bulkEditMode && !canPermanentDelete}
                        onToggleMarked={bulkEditMode ? () => canPermanentDelete && handleToggleSelection(item.id) : undefined}
                        onToggleActive={bulkEditMode ? () => canPermanentDelete && handleToggleSelection(item.id) : () => {}}
                        onShowDetails={bulkEditMode ? undefined : () => handleRestore(item)}
                        detailLabel="もとに戻す"
                        detailIconPath="/diagram_dash_up_then_down.svg"
                        detailIconRotated={true}
                        deleteAriaLabel="完全に削除"
                        onDelete={!bulkEditMode && canPermanentDelete ? () => handlePermanentDelete(item) : undefined}
                        deleteDisabled={!canPermanentDelete}
                        deleteDisabledTooltip="ユーザー権限では共有フォルダ素材を完全に削除できません"
                        deletedDateTime={deletedDateTime}
                    />
                  );
                })}
              </div>
            )}

            {/* ページングボタン */}
            {filteredMaterials.length > 0 && (tempQuery.data?.has_more || cloudQuery.data?.has_more) && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <button
                  type="button"
                  className="btn-modal"
                  onClick={() => {
                    if (tempQuery.data?.has_more && tempQuery.data?.next_cursor) {
                      setTempCursor(tempQuery.data.next_cursor);
                    }
                    if (cloudQuery.data?.has_more && cloudQuery.data?.next_cursor) {
                      setCloudCursor(cloudQuery.data.next_cursor);
                    }
                  }}
                  disabled={tempQuery.isLoading || cloudQuery.isLoading}
                >
                  {tempQuery.isLoading || cloudQuery.isLoading ? '読み込み中...' : 'もっと見る'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* === シナリオタブ === */}
      {activeTab === 'scenarios' && (
        <>
          {/* アクションボタン */}
          <div className={styles.row2}>
            <div className={styles.leftActions}>
              <button
                type="button"
                className={`btn-modal ${bulkEditMode ? styles.actionButtonActive : ''}`}
                onClick={() => {
                  setBulkEditMode(!bulkEditMode);
                  if (bulkEditMode) {
                    setBulkSelectedIds(new Set());
                  }
                }}
              >
                {bulkEditMode ? '一括編集を終了' : '一括編集'}
              </button>
            </div>
            <div className={styles.rightActions}>
              <button
                type="button"
                className="btn-modal-danger"
                onClick={handleEmptyScenarioTrash}
                disabled={deletedScenarios.length === 0}
              >
                ごみ箱を空にする
              </button>
            </div>
          </div>

          {/* 一括編集バー */}
          {bulkEditMode && (
            <BulkActionsBar
              selectedCount={bulkSelectedIds.size}
              onCancel={() => {
                setBulkEditMode(false);
                setBulkSelectedIds(new Set());
              }}
            >
              <button
                type="button"
                className="bulk-action-btn"
                onClick={handleSelectAllScenarios}
              >
                {bulkSelectedIds.size === deletedScenarios.length ? 'すべて解除' : 'すべて選択'}
              </button>
              <button
                type="button"
                className="bulk-action-btn bulk-delete-btn"
                onClick={handleBulkDeleteScenarios}
                disabled={bulkSelectedIds.size === 0}
              >
                完全に削除
              </button>
            </BulkActionsBar>
          )}

          {/* シナリオリスト */}
          <div className={styles.content}>
            {scenarioTrashQuery.isLoading ? (
              <div className={styles.emptyState}>
                <p>読み込み中...</p>
              </div>
            ) : deletedScenarios.length === 0 ? (
              <div className={styles.emptyState}>
                <p>ゴミ箱は空です</p>
              </div>
            ) : (
              <div className={styles.scenarioList}>
                {deletedScenarios.map((scenario) => {
                  const thumbnail = scenario.scenes?.[0]?.thumbnail_url || scenario.scenes?.[0]?.image_url;
                  const deletedDateTime = scenario.deleted_at
                    ? new Date(scenario.deleted_at).toLocaleString('ja-JP', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '';

                  return (
                    <div
                      key={scenario.id}
                      className={`${styles.scenarioCard} ${bulkEditMode && bulkSelectedIds.has(scenario.id) ? styles.scenarioCardSelected : ''}`}
                      onClick={bulkEditMode ? () => handleToggleSelection(scenario.id) : undefined}
                    >
                      {bulkEditMode && (
                        <input
                          type="checkbox"
                          checked={bulkSelectedIds.has(scenario.id)}
                          onChange={() => handleToggleSelection(scenario.id)}
                          className={styles.scenarioCheckbox}
                        />
                      )}
                      <div className={styles.scenarioThumbnail}>
                        {thumbnail ? (
                          <img src={thumbnail} alt="" />
                        ) : (
                          <div className={styles.scenarioNoThumbnail}>No Image</div>
                        )}
                      </div>
                      <div className={styles.scenarioInfo}>
                        <div className={styles.scenarioTitle}>{scenario.title}</div>
                        {scenario.summary && (
                          <div className={styles.scenarioSummary}>{scenario.summary}</div>
                        )}
                        <div className={styles.scenarioMeta}>
                          <span>{scenario.scene_count ?? scenario.scenes?.length ?? 0}シーン</span>
                          {deletedDateTime && <span>削除: {deletedDateTime}</span>}
                        </div>
                      </div>
                      {!bulkEditMode && (
                        <div className={styles.scenarioActions}>
                          <button
                            type="button"
                            className="btn-modal"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreScenario(scenario);
                            }}
                          >
                            復元
                          </button>
                          <button
                            type="button"
                            className="btn-modal-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePermanentDeleteScenario(scenario);
                            }}
                          >
                            完全削除
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ページング */}
            {(scenarioTrashQuery.data?.total ?? 0) > scenarioPage * 50 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <button
                  type="button"
                  className="btn-modal"
                  onClick={() => setScenarioPage((prev) => prev + 1)}
                  disabled={scenarioTrashQuery.isLoading}
                >
                  もっと見る
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
